import { useMemo } from "react";

import clsx from "clsx";

import { useDesignElementManager } from "@hooks/use-design-element-manager";
import {
  type GetLandingPageReportChart_me_landingPageReportChart_rows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { metrics } from "@north-beam/nb-common";
import { MetricValue } from "@shared/metric-value";

interface Props {
  index: number;
  metric: SalesPageMetric;
  active: boolean;
  rows: GetLandingPageReportChart_me_landingPageReportChart_rows[];
  isSelectable: boolean;
  isActiveDisabled: boolean;
  onChange: () => void;
}

export const MetricCard = ({
  index,
  metric,
  active,
  rows,
  isSelectable,
  isActiveDisabled,
  onChange,
}: Props) => {
  const { designElementManager } = useDesignElementManager();
  const labelColor = designElementManager.getColorForIndex(index);

  const value = useMemo(() => {
    const metricPoint = metrics.newMetricPoint();

    for (const row of rows) {
      for (const metric of row.metrics) {
        metrics.addToMetricPoint(metricPoint, metric);
      }
    }

    return metric.calculate(metricPoint) ?? 0;
  }, [metric, rows]);

  const name =
    metric.metadata.overviewPageMetricName ||
    metric.metadata.name ||
    metric.name;

  return (
    <div
      className={clsx(
        "border bg-white rounded-lg py-2 pl-4 pr-2 text-left min-w-[100px]",
        {
          "border-cosmic shadow-[0_0_0_1px] shadow-cosmic": active,
          "border-gray": !active,
          "hover:bg-cosmic-5 cursor-pointer":
            (isSelectable || active) && !(active && isActiveDisabled),
          "opacity-50": !isSelectable && !active,
        },
      )}
      onClick={onChange}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <div
            className={clsx("shrink-0 inline-block rounded-sm w-4 h-[3px]")}
            style={{ backgroundColor: labelColor }}
          ></div>
          <div className="font-bold text-xs relative">{name}</div>
        </div>
      </div>
      <div className="h-7 flex items-end">
        <div className="text-cosmic tracking-wide text-base leading-6">
          <MetricValue
            as="span"
            format={metric.format}
            value={value}
            precision="exact"
          />
        </div>
      </div>
    </div>
  );
};
